import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { listBranchDoors } from '../../../api/branch-door';
import DoorSelectModal from '../../common/modal/DoorSelectModal';

// 이용설정 영역과 톤을 맞추기 위한 색상 — TicketEditStyled/PassEditStyled/TrialEditStyled의
// COLORS 팔레트와 동일한 값을 사용한다 (해당 파일들은 export하지 않아 값만 맞춰서 로컬 정의).
const COLORS = {
  primary: '#4AB3BC',
  primaryDark: '#0F7F85',
  gray4: '#A6A6A6',
  gray6: '#737373',
  black: '#141414',
};

// 이용설정 영역의 "?" 정보 아이콘과 동일한 마크업 (각 Styled.js 인라인 svg와 동일)
function InfoCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="6.5" stroke={COLORS.gray4} />
      <text x="7" y="11" textAnchor="middle" fontSize="10" fill={COLORS.gray4}>
        ?
      </text>
    </svg>
  );
}

// 상품 등록/수정 폼의 "출입설정" 카드 — 판매지점 선택과 같은 패턴(요약 박스 + "출입문 선택" 버튼 → 체크박스 모달).
// 지점이 출입문 체계로 전환(converted=true)돼 있을 때만 렌더링한다.
// 출입 제한이 없는 문은 모든 상품이 항상 입장 가능하므로 요약에 "항상 가능"으로 함께 보여주고 모달에서 체크 고정,
// 제한이 걸린 문만 선택/해제할 수 있다.
// wrapper: 내용이 렌더링될 때만 감싸는 함수(카드 마크업 등) — 미전환 지점에서 빈 카드가 남지 않게 한다.
function AccessDoorSelect({ branchId, token, selectedDoorIds = [], onChange, wrapper, onConvertedChange }) {
  const [modalOpen, setModalOpen] = useState(false);

  const doorsQuery = useQuery({
    queryKey: ['branch-doors-for-product', branchId],
    queryFn: () => listBranchDoors(branchId, token),
    enabled: !!branchId && !!token,
  });

  const converted = doorsQuery.data?.converted;
  const doors = doorsQuery.data?.doors ?? [];

  // 전환 여부를 부모에 알린다 — 전환 지점은 저장 시 항상 accessGroupIds 를 전송해
  // "전체 해제"가 매핑 유지로 오인되지 않게 한다 (코드리뷰 CONFIRMED).
  useEffect(() => {
    if (doorsQuery.data) onConvertedChange?.(!!doorsQuery.data.converted);
  }, [doorsQuery.data, onConvertedChange]);

  // 저장된 매핑에 미제한(고정) 문 id 가 남아 있으면 제거한다 — 문이 나중에 제한 OFF 로
  // 바뀐 상품은 옛 매핑 id 가 프리필돼 저장 전체가 400 으로 막히고 UI 로 해제할 수도 없다
  // (코드리뷰 CONFIRMED).
  useEffect(() => {
    if (!doorsQuery.data?.converted) return;
    const restrictedIds = new Set(
      (doorsQuery.data.doors ?? []).filter(d => d.restricted).map(d => d.id),
    );
    const filtered = selectedDoorIds.filter(id => restrictedIds.has(id));
    if (filtered.length !== selectedDoorIds.length) onChange(filtered);
  }, [doorsQuery.data, selectedDoorIds, onChange]);

  if (!converted || doors.length === 0) return null;

  const fixedDoors = doors.filter(d => !d.restricted);
  const selectedDoors = doors.filter(d => d.restricted && selectedDoorIds.includes(d.id));

  const summary = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
      {selectedDoors.map(d => (
        <Box
          key={d.id}
          sx={{
            pl: '10px',
            pr: '6px',
            height: 26,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            borderRadius: '13px',
            background: COLORS.primary,
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
          }}>
          {d.doorName}
          <Box
            component="button"
            type="button"
            aria-label={`${d.doorName} 제외`}
            onClick={() => onChange(selectedDoorIds.filter(id => id !== d.id))}
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: 'none',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.25)',
              color: '#fff',
              cursor: 'pointer',
              '&:hover': { background: 'rgba(255,255,255,0.45)' },
            }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </Box>
        </Box>
      ))}
      {fixedDoors.map(d => (
        <Tooltip key={d.id} title="출입 제한이 없는 출입문 — 모든 상품 입장 가능" arrow placement="top">
          <Box
            sx={{
              px: '10px',
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '13px',
              background: '#E3F2F3',
              color: COLORS.primaryDark,
              fontSize: 12,
              fontWeight: 600,
            }}>
            {d.doorName}
            <Box component="span" sx={{ fontSize: 10, opacity: 0.8 }}>항상 가능</Box>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COLORS.black }}>입장 가능 출입문</Typography>
        <Tooltip
          title={
            <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
              출입 제한이 걸린 출입문은 선택한 상품만 입장할 수 있습니다.
              <br />
              출입 제한이 없는 출입문은 모든 상품이 항상 입장 가능해 선택이 고정됩니다.
            </Box>
          }
          arrow
          placement="top">
          <Box sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help' }}>
            <InfoCircleIcon />
          </Box>
        </Tooltip>
      </Box>
      {/* 판매지점 선택과 같은 마크업 — 회색 요약 박스 + 우측 선택 버튼 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            padding: '6px 14px',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            backgroundColor: '#F7F7F7',
            fontSize: '14px',
            color: '#333',
          }}>
          {summary}
        </Box>
        <Box
          onClick={() => setModalOpen(true)}
          sx={{
            flexShrink: 0,
            padding: '0 14px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px',
            border: `1px solid ${COLORS.primary}`,
            color: COLORS.primary,
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: '#EAF7F8' },
          }}>
          출입문 선택
        </Box>
      </Box>
      <DoorSelectModal
        open={modalOpen}
        title="입장 가능 출입문 선택"
        subtitle="출입 제한이 없는 문은 모든 상품이 항상 입장 가능해 체크가 고정됩니다. 제한 문만 선택하세요."
        doors={doors}
        selectedIds={selectedDoorIds}
        onChangeIds={onChange}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );

  return wrapper ? wrapper(content) : content;
}

export default AccessDoorSelect;
